import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { defaultPublication, publicationSchema } from "@reactive-resume/schema";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RichInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { AiActions } from "@/client/components/ai-actions";
import { useResumeStore } from "@/client/stores/resume";

import { SectionDialog } from "../sections/shared/section-dialog";
import { YearSelect } from "../sections/shared/year-select";
import { URLInput } from "../sections/shared/url-input";
import { MastersUnionListEditor } from "../sections/masters-union-summary";

const formSchema = publicationSchema;

type FormValues = z.infer<typeof formSchema>;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const SingleMonthYearPicker = ({
  value,
  onChange,
}: {
  value?: string;
  onChange: (value: string) => void;
}) => {
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");

  useEffect(() => {
    if (!value) {
      setMonth("");
      setYear("");
      return;
    }

    const parts = value.trim().split(" ");
    if (parts.length === 2) {
      const monthIndex = MONTHS_SHORT.findIndex(
        (item) => item.toLowerCase() === parts[0].toLowerCase(),
      );

      if (monthIndex !== -1) {
        setMonth(MONTHS[monthIndex]);
        setYear(parts[1]);
        return;
      }
    }

    setMonth("");
    setYear(value.trim());
  }, [value]);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 51 }, (_, i) => String(currentYear - i));

  const formatValue = (nextMonth: string, nextYear: string) => {
    if (!nextYear) return "";
    if (!nextMonth) return nextYear;

    const monthIndex = MONTHS.indexOf(nextMonth as (typeof MONTHS)[number]);
    if (monthIndex === -1) return nextYear;

    return `${MONTHS_SHORT[monthIndex]} ${nextYear}`;
  };

  return (
    <div className="grid gap-4" style={{gridTemplateColumns: '1fr 1fr'}}>
      <Select
        value={month}
        onValueChange={(nextMonth) => {
          setMonth(nextMonth);
          onChange(formatValue(nextMonth, year));
        }}
      >
        <SelectTrigger className="flex-1 min-w-[180px]">
          <SelectValue placeholder="Month" />
        </SelectTrigger>
        <SelectContent className="[&>div]:max-h-[300px] [&>div]:overflow-auto">
          {MONTHS.map((item) => (
            <SelectItem key={item} value={item}>
              {item}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <YearSelect
        value={year}
        onValueChange={(nextYear) => {
          setYear(nextYear);
          onChange(formatValue(month, nextYear));
        }}
        options={years}
        placeholder="Year"
        searchPlaceholder="Search year"
        className="flex-1 min-w-[180px]"
      />
    </div>
  );
};

export const PublicationsDialog = () => {
  const form = useForm<FormValues>({
    defaultValues: defaultPublication,
    resolver: zodResolver(formSchema),
  });

  const template = useResumeStore((state) => state.resume.data?.metadata?.template);
  const isMastersUnion = template === "mastersunion";

  return (
    <SectionDialog<FormValues> id="publications" form={form} defaultValues={defaultPublication}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {isMastersUnion ? (
          <FormField
            name="url"
            control={form.control}
            render={({ field }) => {
              const nameVal = form.watch("name");
              const raw =
                typeof field.value === "string"
                  ? { label: field.value, href: "" }
                  : (field.value ?? { label: "", href: "" });
              const mergedValue = {
                ...raw,
                label: raw.label?.trim() ? raw.label : (nameVal ?? ""),
              };
              return (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <URLInput
                      {...field}
                      value={mergedValue}
                      placeholder="Publication Title"
                      labelPlaceholder={t`Website`}
                      disableAutoHttps={true}
                      swapLabelAndHref={true}
                      onChange={(value) => {
                        field.onChange(value);
                        form.setValue("name", value.label ?? "", { shouldValidate: true });
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        ) : (
          <FormField
            name="name"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t`Name`}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          name="publisher"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Publisher`}</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Publisher Name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="date"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t`Date`}</FormLabel>
              <FormControl>
                {isMastersUnion ? (
                  <SingleMonthYearPicker value={field.value} onChange={field.onChange} />
                ) : (
                  <Input {...field} placeholder={t`March 2023`} />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {!isMastersUnion && (
          <FormField
            name="url"
            control={form.control}
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>Website</FormLabel>
                <FormControl>
                  <URLInput
                    {...field}
                    value={
                      typeof field.value === "string"
                        ? { label: field.value, href: "" }
                        : (field.value ?? { label: "", href: "" })
                    }
                    placeholder={t({
                      message: "Link label",
                      comment: "Display text for a publication URL before the actual URL is set",
                    })}
                    labelPlaceholder={t`Website`}
                    disableAutoHttps={true}
                    swapLabelAndHref={true}
                    onChange={(value) => field.onChange(value)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          name="summary"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t`Summary`}</FormLabel>
              <FormControl>
                {isMastersUnion ? (
                  <MastersUnionListEditor
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                ) : (
                  <RichInput
                    {...field}
                    content={field.value}
                    onChange={(value) => field.onChange(value)}
                    footer={(editor) => (
                      <AiActions
                        value={editor.getText()}
                        content={editor.getHTML()}
                        onChange={(value) => {
                          editor.commands.setContent(value);
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </SectionDialog>
  );
};
